'use client';

import { FileIcon, Loader2 } from 'lucide-react';
import { trpc } from '../../lib/trpc';
import { Carousel, CarouselContent, CarouselItem } from '../../ui/carousel';
import { LazyAttachmentImage } from './lazy-attachment-image';

interface CommentAttachmentsProps {
  commentId: number;
}

export function CommentAttachments({ commentId }: CommentAttachmentsProps) {
  const { data: files, isLoading } = trpc.files.getForTarget.useQuery(
    { targetType: 'comment', targetId: commentId },
    { enabled: Boolean(commentId) }
  );

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 mt-2 px-1">
        <Loader2 className="size-4 animate-spin text-muted-foreground/40" />
        <span className="text-[10px] text-muted-foreground/40">Loading attachments...</span>
      </div>
    );
  }

  if (!files || files.length === 0) return null;

  // Separate images from other files
  const images = files.filter((f) => f.fileType.startsWith('image/'));
  const others = files.filter((f) => !f.fileType.startsWith('image/'));

  const message = files.length === 1 ? '1 attachment' : `${files.length} attachments`;

  return (
    <div className="mt-3 space-y-2">
      {/* Image Carousel */}
      <p className="text-[10px] text-muted-foreground/40">{message}</p>
      {images.length > 0 && (
        <Carousel
          opts={{
            align: 'start',
            containScroll: 'trimSnaps',
            dragFree: true,
          }}
          className="w-full"
        >
          <CarouselContent className="-ml-2">
            {images.map((file) => (
              <CarouselItem key={file.id} className="pl-2 basis-[60%] min-w-0 max-h-50">
                <LazyAttachmentImage
                  fileId={file.id}
                  fileName={file.fileName}
                  className="w-full max-h-50"
                />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      )}

      {/* Non-image files list */}
      {others.length > 0 && (
        <div className="flex flex-wrap gap-2 px-1">
          {others.map((file) => (
            <div
              key={file.id}
              className="flex items-center gap-2 bg-muted/50 hover:bg-muted border border-transparent hover:border-muted-foreground/20 transition-all rounded-md px-2 py-1 select-none cursor-default group"
            >
              <FileIcon className="size-3 text-muted-foreground" />
              <span className="text-[10px] font-medium truncate max-w-[120px]">
                {file.fileName}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
